import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { Box, List, ListItem, ListItemText, Typography, useTheme } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { NoDataContainer, NoDataDescription, NoDataIcon, NoDataTitle, SummaryChartContainer } from './Overview.styled';

interface SummaryChartProps {
  inspections: IInspection[]
}

const SummaryChart = ({ inspections }: SummaryChartProps) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const noticeColors = useNoticeColors();

  const chartData = useMemo(() => {
    if (!inspections || inspections.length === 0) {
      return [
        { name: t('Common_Successful'), value: 0, color: noticeColors.Ok },
        { name: t('Common_Warning'), value: 0, color: noticeColors.Warning },
        { name: t('Common_Error'), value: 0, color: noticeColors.Error },
        { name: t('Common_NotChecked'), value: 0, color: noticeColors.NotChecked },
      ];
    }

    let ok = 0;
    let warning = 0;
    let error = 0;
    let notChecked = 0;

    inspections.forEach((inspection) => {
      const pois = inspection.pointOfInterests ?? [];

      let hasError = false;
      let hasWarning = false;
      let hasNotChecked = false;
      let allOk = true;

      pois.forEach((poi) => {
        const status = SPInspection.getPOIStatus(poi);
        if (status === 'Error') {
          hasError = true;
          allOk = false;
        } else if (status === 'Warning') {
          hasWarning = true;
          allOk = false;
        } else if (status === 'NotChecked') {
          hasNotChecked = true;
          allOk = false;
        }
      });

      if (hasError) {
        error++;
      } else if (hasWarning) {
        warning++;
      } else if (hasNotChecked) {
        notChecked++;
      } else if (allOk && pois.length > 0) {
        ok++;
      } else {
        notChecked++;
      }
    });

    return [
      { name: t('Common_Successful'), value: ok, color: noticeColors.Ok },
      { name: t('Common_Warning'), value: warning, color: noticeColors.Warning },
      { name: t('Common_Error'), value: error, color: noticeColors.Error },
      { name: t('Common_NotChecked'), value: notChecked, color: noticeColors.NotChecked },
    ];
  }, [inspections, noticeColors.Error, noticeColors.NotChecked, noticeColors.Ok, noticeColors.Warning, t]);

  const isEmpty = !chartData.some(d => d.value);

  return (
    <SummaryChartContainer>
      {isEmpty &&
        <NoDataContainer>
          <NoDataIcon />
          <NoDataTitle variant="h6">{t('Project_Overview_NoData')}</NoDataTitle>
          <NoDataDescription variant="body2">
            {t('Project_Overview_NoData_Subtitle')}
          </NoDataDescription>
        </NoDataContainer>
      }
      {!isEmpty && <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" stroke={theme.palette.divider} innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#1E1E2D',
                border: '1px solid #333',
                borderRadius: 8,
                color: '#F1F1F2',
                fontSize: 12,
              }}
              labelStyle={{
                color: '#FFFFFF',
                fontWeight: 'bold',
              }}
              itemStyle={{
                color: '#E1E1E2',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <List sx={{ width: '100%' }}>
          <ListItem>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Ok, mr: 2 }} />
            <ListItemText primary={t('Common_Successful')} />
            <Typography>{chartData[0].value}</Typography>
          </ListItem>
          <ListItem>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Warning, mr: 2 }} />
            <ListItemText primary={t('Common_Warning')} />
            <Typography>{chartData[1].value}</Typography>
          </ListItem>
          <ListItem>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Error, mr: 2 }} />
            <ListItemText primary={t('Common_Error')} />
            <Typography>{chartData[2].value}</Typography>
          </ListItem>
          <ListItem>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.NotChecked, mr: 2 }} />
            <ListItemText primary={t('Common_NotChecked')} />
            <Typography>{chartData[3].value}</Typography>
          </ListItem>
        </List>
      </Box>
      }
    </SummaryChartContainer>
  );
};

export default SummaryChart;
